import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../toast/toast.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';

export interface CustomerFormData {
  id?: number;
  name: string;
  phone: string;
  email: string;
  address: string;
  gstin: string;
  state: string;
  state_code: string;
  is_active: number;
  is_temporary?: number;
}

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss']
})
export class CustomerFormComponent implements OnInit {
  @Input() show = false;
  @Input() customer: CustomerFormData | null = null;
  @Output() showChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<CustomerFormData>();
  @Output() cancelled = new EventEmitter<void>();

  formData: CustomerFormData = {
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: '',
    state: '',
    state_code: '',
    is_active: 1,
    is_temporary: 0
  };

  isSaving = false;

  constructor(
    private api: ApiService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(): void {
    this.initForm();
  }

  initForm(): void {
    if (this.customer) {
      this.formData = { ...this.customer };
    } else {
      this.formData = {
        name: '',
        phone: '',
        email: '',
        address: '',
        gstin: '',
        state: '',
        state_code: '',
        is_active: 1,
        is_temporary: 0
      };
    }
  }

  close(): void {
    this.show = false;
    this.showChange.emit(false);
    this.cancelled.emit();
  }

  save(): void {
    if (!this.formData.name || !this.formData.name.trim()) {
      this.toast.error('Customer name is required');
      return;
    }

    this.isSaving = true;
    const isEdit = this.formData.id !== null && this.formData.id !== undefined;
    const endpoint = isEdit ? 'customers/customer_update.php' : 'customers/customer_create.php';

    this.api.post(endpoint, this.formData).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.toast.success(isEdit ? 'Customer updated successfully' : 'Customer created successfully');
          
          // Emit the saved customer data (with ID from response if new)
          const savedCustomer = {
            ...this.formData,
            id: response.data?.id || this.formData.id
          };
          
          this.saved.emit(savedCustomer);
          this.close();
        } else {
          this.toast.error(response.message || 'Failed to save customer');
        }
        this.isSaving = false;
      },
      error: (error) => {
        this.toast.error(error.message || 'Failed to save customer');
        this.isSaving = false;
      }
    });
  }

  delete(): void {
    if (!this.formData.id) return;

    this.confirmDialog.confirmDelete('Are you sure you want to delete this customer?', { title: 'Delete Customer?' }).subscribe(confirmed => {
      if (!confirmed) return;

      this.api.post('customers/customer_delete.php', { id: this.formData.id }).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.toast.success('Customer deleted successfully');
            this.saved.emit(this.formData); // Emit to trigger refresh
            this.close();
          } else {
            this.toast.error(response.message || 'Failed to delete customer');
          }
        },
        error: (error) => {
          this.toast.error(error.message || 'Failed to delete customer');
        }
      });
    });
  }
}
